/**
 * Neo Product Card 1 – Web component
 * Fetches product via GraphQL, renders Swiper slides + color swatches, caches by product ID.
 * Clicking a color swatch updates the card UI and Swiper to that variant product.
 * No Shadow DOM.
 *
 * Swiper (media carousel) is enabled only when the element has:
 * - data-enable-swiper="true" – initialises Swiper and shows multiple slides;
 *   if missing or not "true", _initSwiper() returns without creating Swiper and
 *   _renderSwiper() outputs only the first slide.
 * - data-enable-pagination="true" – shows pagination dots when slide count > 1;
 *   only has effect when data-enable-swiper is "true".
 */

// @ts-nocheck
// @ts-ignore

(() => {
  if (typeof globalThis !== 'undefined' && !globalThis.AtcPriceBlock) {
    const formatRupees = (amount) => {
      const value = Number(amount) || 0;
      return '₹ ' + new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.floor(value));
    };
    const discountPercent = (price, compareAt) => {
      const sale = Number(price) || 0;
      const compare = Number(compareAt) || 0;
      if (!(compare > sale) || sale <= 0) return 0;
      return Math.round(((compare - sale) / compare) * 100);
    };
    const html = (price, compareAt) => {
      const percent = discountPercent(price, compareAt);
      const saleHtml = `<span class="atc-price-block__sale">${formatRupees(price)}</span>`;
      if (percent <= 0) return `<span class="atc-price-block">${saleHtml}</span>`;
      return `<span class="atc-price-block"><span class="atc-price-block__compare">${formatRupees(compareAt)}</span>${saleHtml}<span class="atc-price-block__badge">${percent}% OFF</span></span>`;
    };
    globalThis.AtcPriceBlock = {
      formatRupees,
      discountPercent,
      html,
      htmlFromCents: (priceCents, compareCents) => html((Number(priceCents) || 0) / 100, (Number(compareCents) || 0) / 100),
    };
  }

  const PRODUCT_FIELDS = `
    title
    handle
    id
    tags
    availableForSale
    productType
    priceRange {
      maxVariantPrice { amount }
      minVariantPrice { amount }
    }
    compareAtPriceRange {
      minVariantPrice { amount }
    }
    selectedOrFirstAvailableVariant {
      price { amount }
      compareAtPrice { amount }
    }
    media(first: 20) {
      edges {
        node {
          mediaContentType
          ... on MediaImage {
            image {
              url
              altText
              width
              height
            }
          }
          ... on Video {
            sources {
              url
              mimeType
            }
            previewImage {
              url
            }
          }
        }
      }
    }
    metafield_tag: metafield(namespace: "neo", key: "usage_tag") {
      value
    }
    metafield_highlights: metafield(namespace: "neo", key: "highlights") {
      value
    }
    metafield_watch_gif: metafield(namespace: "neo", key: "watch_gif") {
      reference {
        ... on MediaImage {
          image { url }
        }
      }
    }
    metafield_color_swatch_image: metafield(namespace: "custom", key: "color_swatch_image") {
      reference {
        ... on MediaImage {
          image { url }
        }
      }
    }
  `;

  const FINAL_ALL_PRODUCT_FIELDS = `
    ${PRODUCT_FIELDS}
    metafield_color_variants: metafield(namespace: "custom", key: "color_variants") {
      references(first: 25) {
        nodes {
          ... on Product {
            ${PRODUCT_FIELDS}
          }
        }
      }
    }
  `;

  /** Global cache: productId (numeric string) -> product object */
  if (typeof globalThis !== 'undefined' && !globalThis.neoProductCardCache) {
    globalThis.neoProductCardCache = {};
  }

  class NeoProductCard1 extends HTMLElement {
    constructor() {
      super();
      this._productId = '';
      this._currentProductId = '';
      this._swiper = null;
      this._swiperInitialized = false;
      this._swiperIntersectionObserver = null;
      /** When showing a color variant from cache, reuse this list (from initial/fetched product that has color_variants). */
      this._colorVariantSwatches = [];
      this._boundOnColorClick = this._onColorSwatchClick.bind(this);
      this._boundOnWatchGifClick = this._onWatchGifClick.bind(this);
      this._watchGifOriginalHTML = null;
      this._watchOverlay = null;
      this._watchOverlayVideo = null;
      this._showWatchGif = true;
      this._showWatchGifVideo = true;
    }

    static get observedAttributes() {
      return ['data-product-id'];
    }

    isSwiperEnabled() {
      return this.getAttribute('data-enable-swiper')?.toLowerCase() === 'true';
    }

    getProductIdGid(productId) {
      if (!productId) return '';
      const num = String(productId).replace(/\D/g, '');
      return num ? `gid://shopify/Product/${num}` : '';
    }

    getProductIdNumeric(productId) {
      if (!productId) return '';
      if (/^\d+$/.test(String(productId).trim())) return String(productId).trim();
      const m = String(productId).match(/gid:\/\/shopify\/Product\/(\d+)/);
      return m ? m[1] : '';
    }

    _sameProductId(a, b) {
      const left = this.getProductIdNumeric(a);
      const right = this.getProductIdNumeric(b);
      return !!(left && right && left === right);
    }

    getHighlightsString(product) {
      const raw = product.metafield_highlights?.value;
      if (raw == null) return '';
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed.join(' | ') : String(parsed);
      } catch {
        return String(raw);
      }
    }

    getWatchGifAndHasVideo(product) {
      if (!this.isSwiperEnabled()) {
        return { watchGifSrc: '', hasVideo: false };
      }
      const mediaEdges = product.media?.edges || [];
      let watchGifSrc = '';
      const watchGifRef = product.metafield_watch_gif?.reference?.image?.url;
      if (watchGifRef) watchGifSrc = watchGifRef;

      let hasVideo = false;
      for (let i = 0; i < mediaEdges.length; i++) {
        const node = mediaEdges[i]?.node;
        if (i <= 2 || !node) continue;
        const isVideo = node.mediaContentType === 'VIDEO' || (node.sources && !node.image);
        if (isVideo) {
          hasVideo = true;
          if (!watchGifSrc && node.previewImage?.url) watchGifSrc = node.previewImage.url;
          break;
        }
      }
      return { watchGifSrc, hasVideo };
    }

    /** Build slides from product media: images first, then videos always at last index. */
    getMediaSlides(product) {
      const edges = product.media?.edges || [];
      const imageSlides = [];
      const videoSlides = [];
      for (const edge of edges) {
        const node = edge?.node;
        if (!node) continue;
        if (node.mediaContentType === 'IMAGE' && node.image) {
          const url = node.image.url;
          const alt = node.image.altText || product.title || '';
          const sep = url.includes('?') ? '&' : '?';
          imageSlides.push({ type: 'image', url: `${url}${sep}width=700`, alt });
        } else if (this.isSwiperEnabled() && node.mediaContentType === 'VIDEO' && node.sources?.length) {
          const src = node.sources.find((s) => s.mimeType?.includes('mp4')) || node.sources[0];
          const poster = node.previewImage?.url;
          if (src?.url) {
            videoSlides.push({
              type: 'video',
              url: src.url,
              mimeType: src.mimeType || 'video/mp4',
              poster: poster ? poster + (poster.includes('?') ? '&' : '?') + 'width=700' : ''
            });
          }
        }
      }
      return [...imageSlides, ...videoSlides];
    }

    /** Primary image for non-swiper mode (first IMAGE in media). */
    getPrimaryImage(product) {
      const edges = product.media?.edges || [];
      for (const edge of edges) {
        const node = edge?.node;
        if (!node) continue;
        if (node.mediaContentType === 'IMAGE' && node.image?.url) {
          const url = node.image.url;
          const alt = node.image.altText || product.title || '';
          const sep = url.includes('?') ? '&' : '?';
          return { url: `${url}${sep}width=700`, alt };
        }
      }
      return null;
    }

    /** Up to 4 color variant swatches with data-product-id (numeric) */
    getColorSwatches(product) {
      const nodes = product.metafield_color_variants?.references?.nodes || [];
      const out = [];

      const currentId = this.getProductIdNumeric(product.id);

      /*
        -----------------------------------
        ADD CURRENT PRODUCT FIRST
        -----------------------------------
      */

      const currentSwatch = product.metafield_color_swatch_image?.reference?.image?.url;

      if (currentSwatch) {
        out.push({
          id: currentId,
          url: currentSwatch,
          product
        });
      }

      /*
        -----------------------------------
        ADD COLOR VARIANTS
        -----------------------------------
      */

      for (const node of nodes) {
        if (!node) continue;

        const id = this.getProductIdNumeric(node.id);

        // prevent duplicate self product
        if (id === currentId) continue;

        const imgUrl =
          node.metafield_color_swatch_image?.reference?.image?.url;

        if (!imgUrl) continue;

        out.push({
          id,
          url: imgUrl,
          product: node
        });
      }

      return out;
    }

    getColorVariantIds(product) {
      const nodes = product.metafield_color_variants?.references?.nodes || [];
      return nodes.map((n) => this.getProductIdNumeric(n?.id)).filter(Boolean).join(',');
    }

    async fetchProduct(productId) {
      const gid = this.getProductIdGid(productId);
      if (!gid) return null;

      const query = `query Product($id: ID!) {
          product(id: $id) {
            ${FINAL_ALL_PRODUCT_FIELDS}
          }
        }`;

      const token = typeof window !== 'undefined' && window.storefrontAccessToken;
      if (!token) {
        console.warn('neo-product-card-1: window.storefrontAccessToken not set');
        return null;
      }

      const res = await fetch('/api/2023-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query, variables: { id: gid } })
      });

      if (!res.ok) return null;
      const json = await res.json();
      if (json.errors || !json.data?.product) return null;
      const product = json.data.product;


      const numId = this.getProductIdNumeric(product.id);
      if (numId && globalThis.neoProductCardCache) {
        globalThis.neoProductCardCache[numId] = product;
        this._cacheColorVariantNodes(product);
      }
      return product;
    }

    /**
     * Nested color-variant nodes only include PRODUCT_FIELDS (no color_variants list).
     * A full fetch always has the metafield_color_variants key (value may be null/empty).
     * @param {object|null|undefined} product
     * @returns {boolean}
     */
    _isFullyCachedProduct(product) {
      return !!(product && Object.prototype.hasOwnProperty.call(product, 'metafield_color_variants'));
    }

    /**
     * Resolves a product from cache or Storefront GraphQL.
     * Partial sibling nodes must not satisfy a full load (they only keep 1 swatch = self).
     * @param {string} productId
     * @returns {Promise<object|null>}
     */
    async _resolveFullProduct(productId) {
      const id = this.getProductIdNumeric(productId);
      if (!id) return null;
      const cached = globalThis.neoProductCardCache?.[id];
      if (this._isFullyCachedProduct(cached)) return cached;
      return this.fetchProduct(id);
    }

    /** Cache each color-variant product node so swatch clicks avoid extra fetches. */
    _cacheColorVariantNodes(product) {
      if (!product || !globalThis.neoProductCardCache) return;
      const nodes = product.metafield_color_variants?.references?.nodes || [];
      for (const node of nodes) {
        const nid = this.getProductIdNumeric(node?.id);
        if (!nid) continue;
        // Never overwrite a full product with a nested partial node — after filters
        // Wizzy often returns a sibling id that would otherwise only render 1 swatch.
        if (this._isFullyCachedProduct(globalThis.neoProductCardCache[nid])) continue;
        globalThis.neoProductCardCache[nid] = node;
      }
    }

    connectedCallback() {
      this._productId = (this.getAttribute('data-product-id') || '').trim();
      if (!this._productId) return;

      this._currentProductId = this._productId;
      if (!this.isSwiperEnabled()) {
        this._showWatchGif = false;
        this._showWatchGifVideo = false;
      }
      this._attachColorSwatchDelegation();
      this._loadAndRender();
    }

    disconnectedCallback() {
      if (this._swiperIntersectionObserver) {
        this._swiperIntersectionObserver.disconnect();
        this._swiperIntersectionObserver = null;
      }
      if (this._swiper && this._swiper.destroy) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this.removeEventListener('click', this._boundOnColorClick, true);
      this.removeAttribute('data-swatch-delegation');
    }

    _attachColorSwatchDelegation() {
      if (this.hasAttribute('data-swatch-delegation')) return;
      this.setAttribute('data-swatch-delegation', '1');
      // Capture phase on host so swatch clicks are not swallowed by nested links/swiper.
      this.addEventListener('click', this._boundOnColorClick, true);
    }

    _attachWishlistClickToSyncCart() {
      let wishlistButton = this.querySelector('.smartwishlist');

      if (wishlistButton) {
        if (wishlistButton.dataset.wishlistBound === '1') return;
        wishlistButton.dataset.wishlistBound = '1';

        wishlistButton.addEventListener('click', (e) => {
          const productId = e.target.closest('[data-product]').getAttribute('data-product');
          const product = globalThis.neoProductCardCache && globalThis.neoProductCardCache[productId];

          if (!product) {
            console.warn("product not found in cache for id", productId);
            return;
          }

          // product object from cache does not have variants,
          // so we fall back to null for variant-level fields
          const firstImage = product.media?.edges?.[0]?.node?.image?.url || null;
          const minPrice = product.priceRange?.minVariantPrice?.amount
            ? parseFloat(product.priceRange.minVariantPrice.amount)
            : null;
          const maxPrice = product.priceRange?.maxVariantPrice?.amount
            ? parseFloat(product.priceRange.maxVariantPrice.amount)
            : null;

          const payload = {
            title: product.title || null,
            handle: product.handle || null,
            vendor: product.vendor || null,  // not in cache object
            variant_id: wishlistButton.dataset.variant || null,  // set via data-variant on the span
            is_true_variant: null, // not available in cache
            variant_count: null, // variants not fetched in this query
            variant_title: null, // variants not fetched in this query
            variant_price: minPrice,
            variant_compare_at_price: maxPrice,
            variant_sku: null, // not in cache object
            variant_out_of_stock: product.availableForSale === false ? true : null,
            variant_inventory_policy: null, // not in cache object
            variant_inventory_quantity: null, // not in cache object
            tags: Array.isArray(product.tags)
              ? product.tags.join(',')
              : null,
            image: firstImage,
          };


          if (typeof handleWishlistStateAndUpdateCart === 'function') {
            handleWishlistStateAndUpdateCart(e, payload);
          } else {
            console.warn("handleWishlistStateAndUpdateCart is not defined");
          }
        });
      } else {
        console.warn("wishlist button not found");
      }
    }

    async _loadAndRender() {
      const product = await this._resolveFullProduct(this._productId);
      if (!product) return;

      const canonicalId = this.getProductIdNumeric(product.id);
      if (canonicalId) {
        this._productId = canonicalId;
        this._currentProductId = canonicalId;
        this.setAttribute('data-product-id', canonicalId);
      }

      this._cacheColorVariantNodes(product);
      const swatches = this.getColorSwatches(product);
      if (swatches.length > this._colorVariantSwatches.length) {
        this._colorVariantSwatches = swatches;
      }

      // ponytail: search/Wizzy only supplies product id — hydrate the full card from GraphQL once.
      this._updateCardUI(product, product, { renderSwatches: true });
      this._attachColorSwatchDelegation();
      this._attachWatchGifClick();
      this._attachWishlistClickToSyncCart();
    }

    _attachWatchGifClick() {
      const w = this.querySelector('.neo-product-card-1-watch-gif-container');
      if (w && !w.hasAttribute('data-watch-click-bound')) {
        w.setAttribute('data-watch-click-bound', '1');
        w.addEventListener('click', this._boundOnWatchGifClick);
      }
    }

    _renderColorSwatches(product, invoker = 'initial') {
      const productId = this.getProductIdNumeric(product.id);
      const container = this.querySelector('.color-variant-images');
      if (!container) return;

      let swatches = this.getColorSwatches(product);
      // Prefer the fuller list kept from the first full product hydrate (filters can
      // re-render a sibling whose nested cache only has its own swatch image).
      if (this._colorVariantSwatches.length > swatches.length) {
        swatches = this._colorVariantSwatches;
      }
      const currentId = this._currentProductId;

      const fragment = document.createDocumentFragment();
      let count = 0;
      let circleCon = null;

      for (const sw of swatches) {
        count++;
        if (count <= 3) {
          const img = document.createElement('img');
          const url = sw.url + (sw.url.includes('?') ? '&' : '?') + 'width=100';

          img.src = url;
          img.alt = product.title || '';
          img.className = 'neo-product-card-color-variant-image';
          img.loading = 'lazy';
          img.setAttribute('data-product-id', sw.id);

          if (this._sameProductId(sw.id, currentId)) img.classList.add('active');

          fragment.appendChild(img);
        }

        if (count === 4) {
          circleCon = document.createElement('div');

          circleCon.style.cssText =
            'width:24px;height:24px;background:#2A2928;border-radius:50%;color:#fff;font-size:11px;display:flex;justify-content:center;align-items:center;font-family : "Athletics-Bold";'

          circleCon.classList.add('uniqueSwatchCon');
          circleCon.setAttribute('onclick', `ATBModalOpen(event, '${productId}')`)

          fragment.appendChild(circleCon);
        }
      }

      if (circleCon && count > 3) {
        circleCon.innerHTML = `
          <span class="loader-color-swatch-count">+${count - 3}</span>
          <span class="loader-color-swatch">
            <svg class="loading-svg" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_aj0A{transform-origin:center;animation:spinner_KYSC .75s infinite linear}@keyframes spinner_KYSC{100%{transform:rotate(360deg)}}</style><path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A"></path></svg>
          </span>
        `
          ;
      }

      const isHidden = count === 0;

      requestAnimationFrame(() => {
        container.innerHTML = '';
        container.appendChild(fragment);
        container.hidden = isHidden;
      });
    }

    /** Toggle active ring only — swatch images/order stay unchanged on variant switch. */
    _updateActiveColorSwatch() {
      const currentId = this._currentProductId;
      this.querySelectorAll('.neo-product-card-color-variant-image').forEach((img) => {
        const swatchId = this.getProductIdNumeric(img.getAttribute('data-product-id') || '');
        img.classList.toggle('active', this._sameProductId(swatchId, currentId));
      });
    }

    _renderSwiper(product, change = 'render') {
      const productUrl = `/products/${product.handle}`;
      const wrapper = this.querySelector('.swiper .swiper-wrapper');
      if (!wrapper) return;

      const enableSwiper = this.isSwiperEnabled();
      let slides = this.getMediaSlides(product);
      if (slides.length === 0) {
        const featured = product.media?.edges?.[0]?.node;
        const url = featured?.image?.url || featured?.previewImage?.url;
        if (url) {
          const sep = url.includes('?') ? '&' : '?';
          slides.push({ url: `${url}${sep}width=700`, alt: product.title || '' });
        }
      }
      if (!enableSwiper && slides.length > 0) {
        slides = slides.slice(0, 1);
      }

      let slidesHTML = slides.map((s, i) => {
        // Skip first slide ONLY on initial render
        if (change !== 'update' && i === 0) return '';

        if (s.type === 'video' && this._showWatchGifVideo) {
          const posterAttr = s.poster ? ` poster="${s.poster.replace(/"/g, '&quot;')}"` : '';
          return `<div class="swiper-slide">
            <a href="${productUrl}" class="neo-product-card-1__image-link">
              <video class="neo-product-card-swiper__video neo-product-card-1__video"
                    width="700"
                    height="1000"
                    ${posterAttr}
                    muted="true"
                    playsinline
                    loop
                    preload="metadata">
                <source src="${s.url.replace(/"/g, '&quot;')}"
                        type="${(s.mimeType || 'video/mp4').replace(/"/g, '&quot;')}">
              </video>
            </a>
          </div>`;
        }

        return `<div class="swiper-slide">
          <a href="${productUrl}" class="neo-product-card-1__image-link">
            <img src="${s.url.replace(/"/g, '&quot;')}&width=520"
              alt="${(s.alt || '').replace(/"/g, '&quot;')}"
              class="neo-product-card-1__image-img"
              width="700"
              height="503">
          </a>
        </div>`;
      }).join('');
      if (change === 'update') {
        wrapper.innerHTML = slidesHTML;
      } else {
        wrapper.innerHTML += slidesHTML;
      }
    }

    /** Defer Swiper construction until the card is about to enter the viewport (400px margin). */
    _scheduleSwiperInit(product, change = 'render') {
      if (this._swiperInitialized || this._swiperIntersectionObserver) return;

      if (typeof IntersectionObserver === 'undefined') {
        this._initSwiper(product, change);
        this._swiperInitialized = true;
        return;
      }

      this._swiperIntersectionObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this._swiperIntersectionObserver.disconnect();
        this._swiperIntersectionObserver = null;
        this._initSwiper(product, change);
        this._swiperInitialized = true;
      }, { rootMargin: '400px 0px', threshold: 0 });

      this._swiperIntersectionObserver.observe(this);
    }

    _initSwiper(product, change = 'render') {
      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) return;

      const enableSwiper = this.isSwiperEnabled();
      if (this._swiper && this._swiper.destroy) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      if (!enableSwiper) return;

      if (typeof Swiper === 'undefined') return;

      if (product) this._renderSwiper(product, change);

      const wrapper = swiperEl.querySelector('.swiper-wrapper');
      const slideCount = wrapper?.querySelectorAll('.swiper-slide').length || 0;
      const enablePagination = this.getAttribute('data-enable-pagination')?.toLowerCase() === 'true';
      const showPagination = slideCount > 1 && enablePagination;

      let paginationEl = null;
      const existingPagination = swiperEl.querySelector('.swiper-pagination');
      if (existingPagination) existingPagination.remove();

      if (showPagination) {
        paginationEl = document.createElement('div');
        paginationEl.className = 'swiper-pagination';
        swiperEl.appendChild(paginationEl);
      }

      const syncVideoToActiveSlide = () => {
        if (!wrapper) return;
        const allVideos = wrapper.querySelectorAll('.neo-product-card-1__video');
        allVideos.forEach((v) => {
          try { v.pause(); } catch (_) { }
        });
        const activeSlide = wrapper.querySelector('.swiper-slide-active');
        const activeVideo = activeSlide?.querySelector('.neo-product-card-1__video');
        if (activeVideo && activeVideo instanceof HTMLVideoElement) {
          activeVideo.play().catch(() => { });
        }
      };

      const swiperConfig = {
        slidesPerView: 'auto',
        spaceBetween: 0,
        on: {
          init: syncVideoToActiveSlide,
          slideChangeTransitionEnd: syncVideoToActiveSlide
        }
      };
      if (showPagination && paginationEl) {
        swiperConfig.pagination = { el: paginationEl, clickable: true };
      }

      this._swiper = new Swiper(swiperEl, swiperConfig);
    }

    _dismissWatchOverlayForSwatchChange() {
      const container = this.querySelector('.neo-product-card-1-watch-gif-container');
      if (!container?.classList.contains('animatation-watch-gif')) return;

      if (this._watchOverlay) {
        this._revertWatchGifState();
        return;
      }

      if (this._watchGifOriginalHTML) {
        container.innerHTML = this._watchGifOriginalHTML;
        this._watchGifOriginalHTML = null;
      }
      container.classList.remove('animatation-watch-gif');
      container.classList.add('animation-watch-gif-close');
      this.querySelector('.neo-product-card-media')
        ?.classList.remove('neo-product-card-media--watch-overlay-active');
    }

    _onColorSwatchClick(e) {
      const img = e.target.closest('.neo-product-card-color-variant-image');
      if (!img || !this.contains(img)) return;

      const colorVariantId = this.getProductIdNumeric(img.getAttribute('data-product-id') || '');
      if (!colorVariantId || this._sameProductId(colorVariantId, this._currentProductId)) return;

      e.preventDefault();
      e.stopPropagation();

      // INP: apply active class first so the click paints quickly
      this._currentProductId = colorVariantId;
      this._updateActiveColorSwatch();

      const rootId = this._productId;
      setTimeout(() => {
        // Ignore stale deferred work if the user clicked another swatch since
        if (!this._sameProductId(colorVariantId, this._currentProductId)) return;
        this._dismissWatchOverlayForSwatchChange();
        this._switchToProduct(rootId, colorVariantId);
      }, 0);
    }

    /**
     * Show or remove the "New" tag inside .neo-product-card-media-container
     * based on whether product.tags includes 'NEW 2026' (matches Liquid condition).
     */
    _updateNewTag(product) {
      const mediaContainer = this.querySelector('.neo-product-card-media-container');
      if (!mediaContainer) return;

      let newTagEl = this.querySelector('.neo-product-card-new-tag');

      const hasNewTag = (product?.tags || [])
        .join(' ')
        .toLowerCase()
        .includes('new 2026');

      if (hasNewTag) {
        if (!newTagEl) {
          newTagEl = document.createElement('p');
          newTagEl.className = 'neo-product-card-new-tag';
          newTagEl.textContent = 'New';
          mediaContainer.appendChild(newTagEl);
        }
      } else {
        if (newTagEl) {
          newTagEl.remove();
        }
      }
    }

    /**
     * Show or remove the "Sale" tag inside .neo-product-card-media-container
     * based on whether product.tags includes 'SALE 2026' (matches Liquid condition).
     */
    _updateSaleTag(product) {
      const mediaContainer = this.querySelector('.neo-product-card-media-container');
      if (!mediaContainer) return;

      let saleTagEl = this.querySelector('.neo-product-card-sale-tag');

      const hasSaleTag = (product?.tags || [])
        .join(' ')
        .toLowerCase()
        .includes('sale 2026');

      if (hasSaleTag) {
        if (!saleTagEl) {
          saleTagEl = document.createElement('p');
          saleTagEl.className = 'neo-product-card-sale-tag';
          saleTagEl.textContent = 'Sale';
          const newTagEl = this.querySelector('.neo-product-card-new-tag');
          if (newTagEl) {
            newTagEl.after(saleTagEl);
          } else {
            mediaContainer.appendChild(saleTagEl);
          }
        }
      } else if (saleTagEl) {
        saleTagEl.remove();
      }
    }

    _onWatchGifClick(e) {
      if (!this.isSwiperEnabled()) return;
      const container = e.target.closest('.neo-product-card-1-watch-gif-container');
      if (!container) return;
      e.preventDefault();
      e.stopPropagation();
      if (container.querySelector('.neo-product-card-1-watch-close-icon')) {
        this._revertWatchGifState();
        return;
      }

      const mediaEl = this.querySelector('.neo-product-card-media');
      const wrapper = this.querySelector('.swiper .swiper-wrapper');
      if (!mediaEl || !wrapper) return;
      const slides = wrapper.querySelectorAll('.swiper-slide');
      let videoSlide = null;
      let firstImageSlide = null;

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        if (!slide) continue;
        if (slide.querySelector('.neo-product-card-1__video')) videoSlide = slide;
        if (!firstImageSlide && slide.querySelector('.neo-product-card-1__image-img')) firstImageSlide = slide;
      }
      if (!videoSlide) return;

      const activeSlide = wrapper.querySelector('.swiper-slide-active');
      const currentImageSlide = activeSlide?.querySelector('.neo-product-card-1__image-img')
        ? activeSlide
        : firstImageSlide;
      const imageElement = currentImageSlide?.querySelector('.neo-product-card-1__image-img') || firstImageSlide?.querySelector('.neo-product-card-1__image-img');
      const videoElement = videoSlide.querySelector('.neo-product-card-1__video');
      if (!imageElement || !(videoElement instanceof HTMLVideoElement)) return;

      const videoSrc = videoElement.currentSrc || videoElement.querySelector('source')?.getAttribute('src') || '';
      const videoPoster = videoElement.getAttribute('poster') || '';
      const videoMime = (videoElement.querySelector('source')?.getAttribute('type')) || 'video/mp4';
      if (!videoSrc) return;

      this._watchGifOriginalHTML = container.innerHTML;
      container.classList.remove('animatation-watch-gif');
      container.classList.add('animation-watch-gif-close');
      const CARD_CLOSE_ICON_SVG = `<svg class="neo-product-card-1-watch-close-icon" width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L6 7M6 1L1 7" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      </svg>`;

      const closeIconHTML = `<div class="relative neo-product-card-1-watch-close-btn">
          ${CARD_CLOSE_ICON_SVG}
        </div>
        <p class="neo-product-card-1-watch-gif-text">Watch</p>`;
      container.innerHTML = closeIconHTML;
      container.classList.add('animatation-watch-gif');
      container.classList.remove('animation-watch-gif-close');

      const overlay = document.createElement('div');
      overlay.className = 'neo-product-card-1-watch-overlay';

      const clonedImage = imageElement.cloneNode(true);
      if (!(clonedImage instanceof HTMLElement)) return;
      clonedImage.classList.add('neo-product-card-1-watch-overlay__image');
      overlay.appendChild(clonedImage);

      const overlayVideo = document.createElement('video');
      overlayVideo.className = 'neo-product-card-swiper__video neo-product-card-1__video neo-product-card-1-watch-overlay__video';
      overlayVideo.setAttribute('muted', 'true');
      overlayVideo.setAttribute('playsinline', '');
      overlayVideo.setAttribute('loop', '');
      overlayVideo.setAttribute('preload', 'metadata');
      if (videoPoster) overlayVideo.setAttribute('poster', videoPoster);
      const source = document.createElement('source');
      source.src = videoSrc;
      source.muted = true;
      source.type = videoMime;
      overlayVideo.appendChild(source);
      overlay.appendChild(overlayVideo);

      mediaEl.classList.add('neo-product-card-media--watch-overlay-active');
      mediaEl.appendChild(overlay);
      this._watchOverlay = overlay;
      this._watchOverlayVideo = overlayVideo;

      requestAnimationFrame(() => {
        clonedImage.classList.add('neo-product-card-1-anim-image-zoom-out');
        overlayVideo.classList.add('neo-product-card-1-anim-video-zoom-in');
        overlayVideo.muted = true;
        overlayVideo.play().catch(() => { });
      });
    }

    _revertWatchGifState() {
      const container = this.querySelector('.neo-product-card-1-watch-gif-container');
      const overlay = this._watchOverlay;
      const overlayVideo = this._watchOverlayVideo;
      if (!container || !this._watchGifOriginalHTML || !overlay) return;

      const clonedImage = overlay.querySelector('img');
      if (clonedImage instanceof HTMLElement && overlayVideo) {
        if (overlayVideo instanceof HTMLVideoElement) {
          try { overlayVideo.pause(); overlayVideo.currentTime = 0; overlayVideo.muted = true; } catch (_) { }
        }
        clonedImage.classList.add('neo-product-card-1-anim-image-zoom-in');
        overlayVideo.classList.add('neo-product-card-1-anim-video-zoom-out');
        setTimeout(() => {
          overlay.remove();
          this._watchOverlay = null;
          this._watchOverlayVideo = null;
          const media = this.querySelector('.neo-product-card-media');
          if (media) media.classList.remove('neo-product-card-media--watch-overlay-active');
          container.innerHTML = this._watchGifOriginalHTML;
          container.classList.remove('animatation-watch-gif');
          container.classList.add('animation-watch-gif-close');
          this._watchGifOriginalHTML = null;
        }, 400);
        return;
      }
      overlay.remove();
      this._watchOverlay = null;
      this._watchOverlayVideo = null;
      const media = this.querySelector('.neo-product-card-media');
      if (media) media.classList.remove('neo-product-card-media--watch-overlay-active');
      container.innerHTML = this._watchGifOriginalHTML;
      container.classList.remove('animatation-watch-gif');
      container.classList.add('animation-watch-gif-close');
      this._watchGifOriginalHTML = null;
    }

    async _switchToProduct(productId = this._productId, colorVariantId) {
      const rootId = this.getProductIdNumeric(productId);
      const variantId = this.getProductIdNumeric(colorVariantId);
      if (!rootId || !variantId) return;

      const product = await this._resolveFullProduct(rootId);
      if (!product) return;

      const swatches = this.getColorSwatches(product);
      if (swatches.length > this._colorVariantSwatches.length) {
        this._colorVariantSwatches = swatches;
      }

      let variantProduct = swatches.find((s) => this._sameProductId(s.id, variantId))?.product;
      if (!variantProduct) {
        variantProduct = globalThis.neoProductCardCache?.[variantId];
      }
      if (!variantProduct) {
        variantProduct = await this.fetchProduct(variantId);
      }
      if (!variantProduct) return;

      this._currentProductId = variantId;
      this._updateCardUI(variantProduct, product);
    }

    _updatePrice(product) {
      const atb = this.querySelector('.add-to-cart-button-gql');
      if (!atb) return;

      const amount = parseFloat(
        product.selectedOrFirstAvailableVariant?.price?.amount ||
        product.priceRange?.minVariantPrice?.amount ||
        0
      );
      if (!amount) return;

      const compareAmount = parseFloat(
        product.selectedOrFirstAvailableVariant?.compareAtPrice?.amount ||
        product.compareAtPriceRange?.minVariantPrice?.amount ||
        0
      );

      const html = globalThis.AtcPriceBlock
        ? globalThis.AtcPriceBlock.html(amount, compareAmount)
        : '';
      if (!html) return;

      requestAnimationFrame(() => {
        let block = atb.querySelector('.atc-price-block');
        if (block) {
          const wrap = document.createElement('div');
          wrap.innerHTML = html;
          const next = wrap.firstElementChild;
          if (next) block.replaceWith(next);
          return;
        }

        const priceSpan = [...atb.children].find(
          (el) =>
            el.tagName === 'SPAN' &&
            !el.classList.contains('atc-link-button__corner') &&
            !el.classList.contains('add-to-cart-button-gql-plus')
        );
        if (!priceSpan) return;
        const wrap = document.createElement('div');
        wrap.innerHTML = html;
        const next = wrap.firstElementChild;
        if (next) priceSpan.replaceWith(next);
      });
    }
    _updateCardUI(colorVariant, product, options = {}) {
      const renderSwatches = options.renderSwatches === true;
      const productUrl = `/products/${colorVariant.handle}`;
      const title = colorVariant.title || '';
      const highlights = this.getHighlightsString(colorVariant);
      const tag = colorVariant.metafield_tag?.value ?? '';
      const enableSwiper = this.isSwiperEnabled();
      const { watchGifSrc, hasVideo } = enableSwiper ? this.getWatchGifAndHasVideo(colorVariant) : { watchGifSrc: '', hasVideo: false };
      const colorVariantIds = this.getColorVariantIds(product);

      if (!renderSwatches) {
        const titleContainer = this.querySelector('.title-container');
        const titleLink = titleContainer?.querySelector('.title-link-wrapper') || this.querySelector('.title-link-wrapper') || titleContainer;
        if (titleLink && titleLink instanceof HTMLAnchorElement) {
          const titleEl = titleLink.querySelector('.neo-product-card-title');
          const subEl = titleLink.querySelector('.neo-product-card-sub-title');

          const hrefChanged = titleLink.getAttribute('href') !== productUrl;
          const titleChanged = titleEl && titleEl.textContent !== title;
          const highlightsChanged = subEl && subEl.textContent !== highlights;

          if (hrefChanged || titleChanged || highlightsChanged) {
            // Writing .href re-evaluates :link/:visited style; writing .textContent
            // tears down and rebuilds text nodes, invalidating layout either way.
            // Skip whatever hasn't actually changed, and batch what has into one
            // rAF so it doesn't block this click's synchronous work.
            requestAnimationFrame(() => {
              if (hrefChanged) titleLink.href = productUrl;
              if (titleChanged) titleEl.textContent = title;
              if (highlightsChanged) subEl.textContent = highlights;
            });
          }
        }

        const tagEl = this.querySelector('.neo-product-card-1-tag');
        if (tag) {
          if (!tagEl) {
            const p = document.createElement('p');
            p.className = 'neo-product-card-1-tag';
            p.textContent = tag;
            this.querySelector('.neo-product-card-media')?.appendChild(p);
          } else {
            if (tagEl.textContent !== tag) tagEl.textContent = tag;
            tagEl.classList.remove('neo-product-card-1-tag--hidden');
          }
        } else if (tagEl && tagEl instanceof HTMLElement) {
          tagEl.classList.add('neo-product-card-1-tag--hidden');
        }
        this._updateNewTag(colorVariant);
        this._updateSaleTag(colorVariant);
      }

      this._updatePrice(colorVariant);

      // Ensure main image link also points to the active color variant
      const mainImg = this.querySelector('.neo-product-card-1__image-img');
      const imgLink = mainImg ? mainImg.closest('a') : null;
      if (imgLink && imgLink instanceof HTMLAnchorElement) {
        if (imgLink.getAttribute('href') !== productUrl) {
          requestAnimationFrame(() => { imgLink.href = productUrl; });
        }
      }

      const watchContainer = this.querySelector('.neo-product-card-1-watch-gif-container');
      if (enableSwiper) {
        if (hasVideo && watchGifSrc) {
          if (!watchContainer) {
            const wrap = document.createElement('div');
            wrap.className = 'neo-product-card-1-watch-gif-container';
            wrap.innerHTML = `
                <div class="relative">
                  <img src="${watchGifSrc + (watchGifSrc.includes('?') ? '&' : '?')}width=100" alt="${(title || '').replace(/"/g, '&quot;')}" class="neo-product-card-1-watch-gif" loading="lazy">
                  <svg class="neo-product-card-1-watch-play-icon" width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <foreignObject x="-9.14844" y="-9.14844" width="24.5459" height="26.1479"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(4.57px);clip-path:url(#bgblur_0_6163_3766_clip_path);height:100%;width:100%"></div></foreignObject>
                    <path data-figma-bg-blur-radius="9.14844" d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z" fill="white"></path>
                    <defs>
                      <clipPath id="bgblur_0_6163_3766_clip_path" transform="translate(9.14844 9.14844)">
                        <path d="M0 7.08734V0.735669C0 0.149096 0.653735 -0.200773 1.14179 0.124599L5.90983 3.30329C6.36248 3.60505 6.36248 4.27019 5.90983 4.57195L1.18526 7.72167C0.67862 8.05943 0 7.69624 0 7.08734Z"></path>
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <p class="neo-product-card-1-watch-gif-text">Watch</p>
              `;
            wrap.addEventListener('click', this._boundOnWatchGifClick);
            const details = this.querySelector('.neo-product-card-details');
            const swatchContainer = this.querySelector('.color-variant-images');
            const mediaContainer = this.querySelector('.neo-product-card-media-container');
            const insertBeforeEl = swatchContainer || mediaContainer || details?.nextSibling;
            if (details?.parentNode && insertBeforeEl) {
              details.parentNode.insertBefore(wrap, insertBeforeEl);
            } else if (mediaContainer?.parentNode) {
              mediaContainer.parentNode.insertBefore(wrap, mediaContainer);
            } else {
              this.insertBefore(wrap, this.firstChild);
            }
          } else {
            watchContainer.classList.remove('neo-product-card-1-watch-gif-container--hidden');
            const img = watchContainer.querySelector('.neo-product-card-1-watch-gif');
            if (img && img instanceof HTMLImageElement) img.src = watchGifSrc + (watchGifSrc.includes('?') ? '&' : '?') + 'width=100';
          }
        } else if (watchContainer && watchContainer instanceof HTMLElement) {
          watchContainer.classList.add('neo-product-card-1-watch-gif-container--hidden');
        }
      } else if (watchContainer && watchContainer instanceof HTMLElement) {
        watchContainer.classList.add('neo-product-card-1-watch-gif-container--hidden');
      }

      if (enableSwiper) {
        const change = renderSwatches ? 'render' : 'update';
        if (renderSwatches) {
          // Initial hydrate: card may still be off-screen, defer Swiper construction (and slide render) together.
          this._scheduleSwiperInit(colorVariant, change);
        } else {
          // Color-swatch switch: card is already visible, so render slides + init synchronously.
          if (this._swiperIntersectionObserver) {
            this._swiperIntersectionObserver.disconnect();
            this._swiperIntersectionObserver = null;
          }
          setTimeout(() => {
            this._initSwiper(colorVariant, change);
          }, 5);
          this._swiperInitialized = true;
        }
      } else {
        const primary = this.getPrimaryImage(colorVariant);
        const mediaContainer = this.querySelector('.neo-product-card-media');
        const imgEl = mediaContainer?.querySelector('.neo-product-card-1__image-img');
        if (primary && imgEl) {
          imgEl.src = primary.url;
          imgEl.alt = primary.alt;
          // Set srcset attribute with multiple widths for responsive images
          const baseUrl = primary.url.split('?')[0];
          const urlParams = primary.url.includes('?') ? primary.url.split('?')[1].replace(/(^|&)width=\d+/, '').replace(/^&/, '') : '';
          const paramConnector = urlParams ? '&' : '';
          const widths = [352, 520];
          imgEl.srcset = widths.map(w => {
            return `${baseUrl}?${urlParams}${paramConnector}${urlParams ? '' : ''}width=${w} ${w}w`;
          }).join(', ');
        }
      }
      const atb = this.querySelector('.add-to-cart-button-gql');
      if (renderSwatches) {
        this._renderColorSwatches(product, 'initial');
      } else {
        if (atb) {
          atb.setAttribute('data-product-id', this._currentProductId);
          atb.setAttribute('data-color-variants-ids', colorVariantIds || '');
          atb.setAttribute('onclick', `ATBModalOpen(event, '${this._currentProductId}')`);
          const smartwishlist = this.querySelector('.smartwishlist');
          if (smartwishlist) smartwishlist.setAttribute('data-product', this._currentProductId);
        }
      }
    }
  }

  if (!customElements.get('neo-product-card-1')) {
    customElements.define('neo-product-card-1', NeoProductCard1);
  }
})();
